import Logger from '@/lib/logger';
import { useEffect, useState } from 'react';

const logger = new Logger('Script loader', 'ERROR').enable();

/**
 * Async wait for a variable till it gets defined in a parent
 *
 * • Usage Example: Waiting for async window.FB var to be initialized
 * - eg: `waitForVar('FB').then(FB => FB.test())`
 * - eg: `waitForVar('FB', { pollFrequency: 500, retries: 2, parent: window || global }).then(FB => FB.test())`
 * - eg: `waitForVar('FB', { retries: ({ retries } => retries * 500) }).then(FB => FB.test())`
 */
export const waitForVar =
  (
    name,
    {
      pollFrequency = 1000,
      retries: inRetries = 100,
      parent = window,
    }: {
      pollFrequency?: number | (({ retries }: { retries: number }) => number);
      retries?: number;
      parent?: object | false;
    } = { pollFrequency: 1000, retries: 100, parent: typeof window !== 'undefined' && window }
  ) =>
  async () => {
    if (!parent) {
      return;
    }
    // eslint-disable-next-line no-prototype-builtins
    if (parent && parent.hasOwnProperty(name)) {
      return parent[name];
    }
    if (!inRetries) {
      return undefined;
    }
    const retries = inRetries - 1;
    await new Promise((resolve) =>
      setTimeout(
        resolve,
        typeof pollFrequency === 'function' ? pollFrequency({ retries }) : pollFrequency
      )
    );
    return waitForVar(name, { pollFrequency, parent, retries });
  };

/**
 * useScript hook
 *
 * Modified by @bamroberts to return hook states
 *
 * https://usehooks.com/useScript
 */
const useScript = (src: string, isReadyCheck: () => Promise<any> | null) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(!!isReadyCheck);

  useEffect(
    () => {
      // Allow falsy src value if waiting on other data needed for constructing the script URL passed to this hook.
      if (!src) {
        return;
      }

      // Fetch existing script element by src
      // It may have been added by another instance of this hook
      let script = document.querySelectorAll<HTMLScriptElement | undefined>(
        `script[src="${src}"]`
      )[0];

      if (script) {
        // console.log('loaded');
        setLoaded(true);
      } else {
        // Create script
        script = document.createElement('script');
        script.src = src;
        script.async = true;

        script.onload = () => {
          // console.log('loaded');
          setLoaded(true);
        };
        script.onerror = () => {
          logger.error(`Error loading script: ${src}`);

          setFailed(true);
        };
        // Add script to document body
        document.body.appendChild(script);
      }

      if (isReadyCheck) {
        // console.log('ready');
        isReadyCheck().then(() => setReady(true));
      }
    },
    [src] // Only re-run effect if script src changes
  );
  return {
    loaded,
    failed,
    ready,
  };
};

export default useScript;
